import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * 卡片內清單的分頁器。
 *
 * 原本是兩顆 26px 的「‹ ›」—— 兩條硬規則各犯一次：手指點不到（要 ≥ 44px），
 * 而且只有符號沒有文字（讀螢幕的人聽到的是「左單引號」）。
 * 現在是兩顆帶字的外框按鈕（高度走 --t-control-h），狀態行跟著字級走。
 * 介面只有 onPrev / onNext，所以不換成跳頁式的 MUI Pagination。
 *
 * 用法：
 *   const { pageItems, pager } = useListPage(items, 5);
 *   {pageItems.map(...)}
 *   {pager}
 */
function ListPager({ page, totalPages, total, onPrev, onNext }) {
  if (totalPages <= 1) return null;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      sx={{
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {/* tabular-nums：翻頁時數字不會因為寬度不同而左右抖動 */}
      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
        第 {page} / {totalPages} 頁 · 共 {total} 筆
      </Typography>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="outlined" disabled={page <= 1} onClick={onPrev}>
          上一頁
        </Button>
        <Button variant="outlined" disabled={page >= totalPages} onClick={onNext}>
          下一頁
        </Button>
      </Stack>
    </Stack>
  );
}

ListPager.propTypes = {
  page: PropTypes.number,
  totalPages: PropTypes.number,
  total: PropTypes.number,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
};

ListPager.defaultProps = {
  page: 1,
  totalPages: 1,
  total: 0,
  onPrev: () => {},
  onNext: () => {},
};

export default ListPager;

/**
 * 把陣列切成分頁並回傳當頁資料與現成的分頁器元素。
 * 資料筆數變動時（例如切換篩選或重新載入）自動把頁碼夾回有效範圍，避免停在空白頁。
 *
 * resetKey（選填）：篩選條件的識別字串（搜尋關鍵字、選定成員、月份…）。
 * 它一變就把頁碼歸零回第 1 頁。只靠上面的「夾回範圍」是不夠的——
 * 停在第 3 頁時輸入搜尋，若命中結果仍有 3 頁以上，頁碼根本不會動，
 * 使用者會看到「搜尋完卻停在第 3 頁、前 20 筆命中結果被藏起來」。
 * 不傳就維持原本行為（僅夾回範圍），既有呼叫端不受影響。
 */
export function useListPage(items, size = 5, resetKey) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const [page, setPage] = useState(1);

  /*
   * 在 render 階段比對並歸零，不用 useEffect：
   * 用 effect 的話會先畫出一格「舊頁碼配新資料」的畫面再跳回第 1 頁，看得出閃動。
   * （React 官方的 "adjusting state when props change" 寫法，setState 後會立刻重跑本元件。）
   */
  const [prevKey, setPrevKey] = useState(resetKey);
  if (resetKey !== prevKey) {
    setPrevKey(resetKey);
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(list.length / size));
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = list.slice((safePage - 1) * size, safePage * size);

  const pager = (
    <ListPager
      page={safePage}
      totalPages={totalPages}
      total={list.length}
      onPrev={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
    />
  );

  return { pageItems, pager, page: safePage, totalPages, total: list.length };
}
